import React, { useState, useMemo } from 'react';
import { FaBoxOpen, FaLayerGroup, FaSearch } from 'react-icons/fa';
import { Card, SectionTitle, CountPill, Spinner, EmptyState } from './Primitives';

/**
 * Step 1: selezione prodotto e filtro moduli.
 * Il prodotto guida il caricamento dei moduli associati; i moduli filtrano le
 * modifiche recenti.
 */
export default function ProductModulePanel({
  products,
  modules,
  modulesLoading,
  productId,
  moduleIds,
  onSelectProduct,
  onToggleModule,
  onSelectAllModules,
  onClearModules,
}) {
  const [moduleQuery, setModuleQuery] = useState('');

  const filteredModules = useMemo(() => {
    const q = moduleQuery.trim().toLowerCase();
    if (!q) return modules;
    return modules.filter(
      (m) =>
        String(m.code || '').toLowerCase().includes(q) ||
        String(m.description || '').toLowerCase().includes(q),
    );
  }, [modules, moduleQuery]);

  return (
    <Card className="p-5">
      <SectionTitle
        icon={FaBoxOpen}
        title="Prodotto e moduli"
        subtitle="Scegli il prodotto e restringi i moduli da considerare"
      />

      <div className="mt-4">
        <label
          htmlFor="gb-product"
          className="mb-1.5 block text-xs font-medium text-gray-600 dark:text-gray-400"
        >
          Prodotto
        </label>
        <select
          id="gb-product"
          value={productId}
          onChange={(e) => onSelectProduct(e.target.value)}
          className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
        >
          <option value="">Tutti i prodotti</option>
          {products.map((p) => (
            <option key={p.id} value={p.id}>
              {p.code ? `${p.code} — ` : ''}
              {p.description || `Prodotto ${p.id}`}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <FaLayerGroup className="text-primary" size={14} />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
              Moduli
            </span>
            {moduleIds.length > 0 && <CountPill>{moduleIds.length}</CountPill>}
          </div>
          <div className="flex items-center gap-2 text-xs">
            <button
              type="button"
              onClick={onSelectAllModules}
              disabled={filteredModules.length === 0}
              className="rounded-md px-2 py-1 text-primary hover:bg-primary/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40"
            >
              Tutti
            </button>
            <button
              type="button"
              onClick={onClearModules}
              disabled={moduleIds.length === 0}
              className="rounded-md px-2 py-1 text-gray-500 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:hover:bg-gray-700 disabled:opacity-40"
            >
              Azzera
            </button>
          </div>
        </div>

        <div className="relative mt-3">
          <FaSearch
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            size={12}
          />
          <input
            type="text"
            value={moduleQuery}
            onChange={(e) => setModuleQuery(e.target.value)}
            placeholder="Filtra moduli…"
            aria-label="Filtra moduli"
            className="w-full rounded-xl border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-800 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
          />
        </div>

        <div className="mt-3">
          {modulesLoading ? (
            <div className="flex items-center gap-2 py-6 text-sm text-gray-500 dark:text-gray-400">
              <Spinner className="h-4 w-4" /> Caricamento moduli…
            </div>
          ) : filteredModules.length === 0 ? (
            <EmptyState
              icon={FaLayerGroup}
              title="Nessun modulo"
              hint="Seleziona un prodotto o rimuovi il filtro testuale."
            />
          ) : (
            <div className="flex max-h-56 flex-wrap gap-2 overflow-y-auto pr-1">
              {filteredModules.map((m) => {
                const active = moduleIds.includes(m.id);
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => onToggleModule(m.id)}
                    aria-pressed={active}
                    className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                      active
                        ? 'border-primary bg-primary text-white shadow-sm'
                        : 'border-gray-300 bg-white text-gray-600 hover:border-primary hover:text-primary dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300 dark:hover:border-primary'
                    }`}
                    title={m.description || m.code}
                  >
                    {m.code || m.description || `Modulo ${m.id}`}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
